import React, { useEffect, useState } from "react";
import { Button, Modal } from "react-bootstrap";
import {
  formatFileSize,
  getPdfName,
  getPdfUrl,
  isPdfFile,
  MAX_PDF_SIZE_BYTES,
  MAX_PDF_SIZE_MB,
} from "../utils/pdfHelpers";

const EmployeePdfEditModal = ({ show, onHide, onSave, saving, pdf }) => {
  const [pdfName, setPdfName] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileErrors, setFileErrors] = useState({});
  const [showCurrentPdfFile, setShowCurrentPdfFile] = useState(true);

  useEffect(() => {
    if (!show) return;

    setPdfName(pdf ? getPdfName(pdf) : "");
    setSelectedFile(null);
    setFileErrors({});
    setShowCurrentPdfFile(true);
  }, [pdf, show]);

  const closeModal = () => {
    setPdfName("");
    setSelectedFile(null);
    setFileErrors({});
    setShowCurrentPdfFile(true);
    onHide();
  };

  const handleFileChange = (event) => {
    const [file] = Array.from(event.target.files || []);
    event.target.value = "";

    if (!file) return;

    if (!isPdfFile(file)) {
      setFileErrors((prev) => ({
        ...prev,
        file: "Only PDF files are allowed.",
      }));
      return;
    }

    setSelectedFile(file);
    setFileErrors((prev) => ({ ...prev, file: null, submit: null }));
  };

  const handleDrop = (event) => {
    event.preventDefault();
    handleFileChange({
      target: { files: event.dataTransfer.files, value: "" },
    });
  };

  const handleSubmit = async () => {
    const errors = {};
    const currentFileUrl =
      showCurrentPdfFile && currentPdfUrl ? currentPdfUrl : "";

    if (!pdfName.trim()) errors.name = "PDF name is required.";
    if (!selectedFile && !currentFileUrl) {
      errors.file = "PDF file is required.";
    }
    if (selectedFile && selectedFile.size > MAX_PDF_SIZE_BYTES) {
      errors.file = `File must be ${MAX_PDF_SIZE_MB} MB or less.`;
    }

    if (Object.keys(errors).length) {
      setFileErrors(errors);
      return;
    }

    const saved = await onSave({
      name: pdfName.trim(),
      file: selectedFile,
      fileUrl: currentFileUrl,
      pdf,
    });

    if (saved?.success || saved === true) {
      closeModal();
      return;
    }

    if (saved?.error) {
      setFileErrors((prev) => ({
        ...prev,
        submit: saved.error,
      }));
    }
  };

  const isOverSize = selectedFile && selectedFile.size > MAX_PDF_SIZE_BYTES;
  const currentPdfName = pdf ? getPdfName(pdf) : "";
  const currentPdfUrl = pdf ? getPdfUrl(pdf) : "";

  return (
    <Modal show={show} onHide={closeModal} centered>
      <Modal.Header
        closeButton
        style={{
          borderBottom: "0.5px solid var(--bs-border-color, #dee2e6)",
          padding: "1rem 1.25rem",
        }}
      >
        <div className="d-flex align-items-center gap-2">
          <div
            style={{
              width: 34,
              height: 34,
              borderRadius: 8,
              background: "#f85c63",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <i
              className="fas fa-edit"
              style={{ color: "white", fontSize: 16 }}
            />
          </div>
          <div>
            <Modal.Title
              style={{ fontSize: 15, fontWeight: 500, marginBottom: 1 }}
            >
              Edit PDF
            </Modal.Title>
            <div style={{ fontSize: 12, color: "#6c757d" }}>
              Update the name or replace this PDF
            </div>
          </div>
        </div>
      </Modal.Header>

      <Modal.Body
        style={{
          padding: "1.25rem",
          display: "flex",
          flexDirection: "column",
          gap: "1.1rem",
          maxHeight: "calc(100vh - 190px)",
          overflowY: "auto",
        }}
      >
        <div>
          <label
            className="form-label"
            style={{ fontSize: 12, fontWeight: 500, color: "#6c757d" }}
          >
            PDF name <span style={{ color: "#D85A30" }}>*</span>
          </label>
          <input
            type="text"
            maxLength={100}
            className={`form-control form-control-sm ${
              fileErrors.name ? "is-invalid" : ""
            }`}
            placeholder="e.g. Employee Handbook 2025"
            value={pdfName}
            onChange={(event) => {
              setPdfName(event.target.value);
              if (event.target.value.trim()) {
                setFileErrors((prev) => ({
                  ...prev,
                  name: null,
                  submit: null,
                }));
              }
            }}
          />
          <div className="d-flex justify-content-between align-items-center mt-1">
            {fileErrors.name ? (
              <div className="text-danger" style={{ fontSize: 11 }}>
                <i className="fas fa-exclamation-circle me-1" />
                {fileErrors.name}
              </div>
            ) : (
              <span />
            )}
            <span style={{ fontSize: 11, color: "#adb5bd" }}>
              {pdfName.length} / 100
            </span>
          </div>
        </div>

        <div>
          <label
            className="form-label"
            style={{ fontSize: 12, fontWeight: 500, color: "#6c757d" }}
          >
            PDF file
          </label>

          {!selectedFile && showCurrentPdfFile && currentPdfName && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                padding: "10px 12px",
                borderRadius: 8,
                border: "0.5px solid #dee2e6",
                background: "#f8f9fa",
                marginBottom: 8,
              }}
            >
              <div
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: 6,
                  background: "white",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <i
                  className="fas fa-file-pdf"
                  style={{ fontSize: 15, color: "#f85c63" }}
                />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    fontSize: 14,
                    fontWeight: 500,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    color: "#111827",
                  }}
                  title={currentPdfName}
                >
                  {currentPdfName}
                </div>
             
              </div>
              <button
                type="button"
                onClick={() => {
                  setShowCurrentPdfFile(false);
                  setFileErrors((prev) => ({ ...prev, submit: null }));
                }}
                style={{
                  width: 24,
                  height: 24,
                  borderRadius: 6,
                  border: "none",
                  background: "none",
                  cursor: "pointer",
                  color: "#adb5bd",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
                aria-label="Hide current PDF"
              >
                <i className="fas fa-times" style={{ fontSize: 12 }} />
              </button>
            </div>
          )}

          {!selectedFile && (
            <label
              style={{
                display: "block",
                border: `1.5px dashed ${
                  fileErrors.file ? "#E24B4A" : "#ced4da"
                }`,
                borderRadius: 8,
                padding: "1.5rem 1rem",
                textAlign: "center",
                cursor: "pointer",
                background: fileErrors.file ? "#FCEBEB" : "transparent",
                transition: "border-color .15s",
              }}
              onDragOver={(event) => event.preventDefault()}
              onDrop={handleDrop}
            >
              <input
                type="file"
                accept="application/pdf"
                onChange={handleFileChange}
                style={{ display: "none" }}
              />
              <div
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: "50%",
                  background: "#f8f9fa",
                  border: "0.5px solid #dee2e6",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  margin: "0 auto 10px",
                }}
              >
                <i
                  className="fas fa-cloud-upload-alt"
                  style={{ fontSize: 18, color: "#6c757d" }}
                />
              </div>
              <div style={{ fontSize: 13, color: "#6c757d" }}>
                Drag & drop or{" "}
                <span style={{ color: "#f85c63", fontWeight: 500 }}>
                  browse one file
                </span>
              </div>
              <div style={{ fontSize: 11, color: "#adb5bd", marginTop: 4 }}>
                Optional · PDF only · Max {MAX_PDF_SIZE_MB} MB
              </div>
            </label>
          )}

          {fileErrors.file && (
            <div
              style={{
                fontSize: 11,
                color: "#E24B4A",
                marginTop: 4,
                display: "flex",
                alignItems: "center",
                gap: 4,
              }}
            >
              <i className="fas fa-exclamation-circle" /> {fileErrors.file}
            </div>
          )}

          {fileErrors.submit && (
            <div
              style={{
                fontSize: 11,
                color: "#E24B4A",
                marginTop: 4,
                display: "flex",
                alignItems: "center",
                gap: 4,
              }}
            >
              <i className="fas fa-exclamation-circle" /> {fileErrors.submit}
            </div>
          )}

          {selectedFile && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                padding: "10px 12px",
                borderRadius: 8,
                border: `0.5px solid ${isOverSize ? "#F09595" : "#dee2e6"}`,
                background: isOverSize ? "#FCEBEB" : "#f8f9fa",
                marginTop: 4,
              }}
            >
              <div
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: 6,
                  background: isOverSize ? "#F7C1C1" : "#FAECE7",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <i
                  className="fas fa-file-pdf"
                  style={{
                    fontSize: 15,
                    color: isOverSize ? "#A32D2D" : "#993C1D",
                  }}
                />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    fontSize: 12,
                    fontWeight: 500,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    color: "#111827",
                  }}
                  title={selectedFile.name}
                >
                  {selectedFile.name}
                </div>
                <div
                  style={{
                    fontSize: 11,
                    color: isOverSize ? "#E24B4A" : "#adb5bd",
                  }}
                >
                  {formatFileSize(selectedFile.size)}
                  {isOverSize ? ` · exceeds ${MAX_PDF_SIZE_MB} MB limit` : ""}
                </div>
              </div>
              <label
                style={{
                  fontSize: 11,
                  color: "#D85A30",
                  fontWeight: 500,
                  cursor: "pointer",
                  whiteSpace: "nowrap",
                  flexShrink: 0,
                }}
                title="Replace file"
              >
                <input
                  type="file"
                  accept="application/pdf"
                  onChange={handleFileChange}
                  style={{ display: "none" }}
                />
                Replace
              </label>
              <button
                type="button"
                onClick={() => {
                  setSelectedFile(null);
                  setFileErrors((prev) => ({
                    ...prev,
                    file: null,
                    submit: null,
                  }));
                }}
                style={{
                  width: 24,
                  height: 24,
                  borderRadius: 6,
                  border: "none",
                  background: "none",
                  cursor: "pointer",
                  color: "#adb5bd",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
                aria-label="Remove selected file"
              >
                <i className="fas fa-times" style={{ fontSize: 12 }} />
              </button>
            </div>
          )}
        </div>
      </Modal.Body>

      <Modal.Footer
        style={{
          borderTop: "0.5px solid var(--bs-border-color, #dee2e6)",
          padding: "1rem 1.25rem",
        }}
      >
        <Button variant="light" onClick={closeModal} style={{ fontSize: 13 }}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={saving}
          style={{
            background: "#f85c63",
            border: "none",
            fontSize: 13,
            fontWeight: 500,
            display: "flex",
            alignItems: "center",
            gap: 6,
          }}
        >
          <i className="fas fa-save" />
          {saving ? "Saving..." : "Update PDF"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default EmployeePdfEditModal;
